import { createFileRoute } from "@tanstack/react-router";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { MonoText } from "@/components/display/mono";
import { EmptyState, LoadingState, ErrorState } from "@/components/feedback/states";
import { useRegions, useTenantRegion } from "@/api/settings";

export const Route = createFileRoute("/_app/settings/regions")({
  component: RegionsSettings,
});

interface Region {
  name: string;
  url?: string | null;
}

function RegionsSettings() {
  const regions = useRegions();
  const pref = useTenantRegion();

  if (regions.isLoading) return <LoadingState />;
  if (regions.error) return <ErrorState error={regions.error} onRetry={() => regions.refetch()} />;

  const p = pref.data;

  const columns: Column<Region>[] = [
    { id: "name", header: "Region", cell: (r) => r.name },
    { id: "url", header: "Endpoint", cell: (r) => <MonoText>{r.url ?? "—"}</MonoText> },
    {
      id: "active",
      header: "",
      align: "right",
      cell: (r) =>
        r.name === p?.preferredRegion ? (
          <Chip size="small" color="primary" label="Preferred" />
        ) : null,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Regions"
        description="Where this tenant's data is processed and stored, and the failover region."
      />
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
              Tenant preference
            </Typography>
            <Stack spacing={1}>
              <Row label="Preferred region" value={p?.preferredRegion ?? "Platform default"} />
              <Row label="Data residency" value={p?.dataResidency ?? "Not pinned"} />
              <Row label="Failover region" value={p?.failoverRegion ?? "—"} />
              {p?.notes ? <Row label="Notes" value={p.notes} /> : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
              Region changes involve data migration and are handled by a system administrator.
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <DataTable
            columns={columns}
            data={regions.data as Region[] | undefined}
            rowKey={(r) => r.name}
            empty={<EmptyState title="No regions configured" />}
          />
        </Card>
      </Stack>
    </PageContainer>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: "space-between" }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
}
